import { useEffect, useMemo, useState } from 'react'
import { api } from '../../lib/api'
import { useI18n } from '../../hooks/useI18n'

/**
 * Pick a restaurant from the catalogue. Port of
 * wechat/src/components/restaurants/RestaurantFinder.jsx — same endpoint, same
 * paging, same answer shape.
 *
 * THE RULE, and the reason this component exists at all: the answer is set
 * ONLY by tapping a card. The text box is a filter, never the answer. Anything
 * else cannot be linked to `restaurants.id` by foreign key when an event is
 * approved — which is exactly how `favorite_restaurant_sh` ended up holding
 * the string "none" from the web app's free-text fallback.
 */

export interface RestaurantAnswer {
  id: string
  restaurant_id: string | null
  name: string
  name_zh: string | null
  address: string | null
  address_zh: string | null
  business_area_id: string | null
  business_area: { name_en?: string; name_zh?: string } | null
}

interface RestaurantRow {
  id?: string
  restaurant_id?: string
  name?: string
  name_zh?: string
  address?: string
  address_zh?: string
  business_area_id?: string
  business_area?: { name_en?: string; name_zh?: string }
  photo_url?: unknown
  rating?: number
  price_per_person?: number
}

/** Exactly the fields the mini program stores, so both apps write one shape. */
export const restaurantAnswer = (r: RestaurantRow): RestaurantAnswer => ({
  id: String(r.id),
  restaurant_id: r.restaurant_id || null,
  name: r.name || '',
  name_zh: r.name_zh || null,
  address: r.address || null,
  address_zh: r.address_zh || null,
  business_area_id: r.business_area_id || null,
  business_area: r.business_area || null
})

export function restaurantAnswerLabel(value: unknown, lang: 'en' | 'zh' = 'en'): string {
  if (!value) return ''
  if (typeof value === 'string') return value
  const v = value as RestaurantAnswer
  const name = lang === 'zh' ? v.name_zh || v.name : v.name || v.name_zh
  const area = v.business_area
  const areaName = area ? (lang === 'zh' ? area.name_zh || area.name_en : area.name_en || area.name_zh) : ''
  return [name, areaName].filter(Boolean).join(' · ')
}

/** photo_url is sometimes a URL, sometimes a JSON array, sometimes junk. */
const firstPhoto = (value: unknown): string => {
  if (!value) return ''
  if (Array.isArray(value)) return typeof value[0] === 'string' ? value[0] : ''
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('http')) return trimmed
  if (trimmed.startsWith('[') || trimmed.startsWith('"')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return typeof parsed[0] === 'string' ? parsed[0] : ''
      return typeof parsed === 'string' && parsed.startsWith('http') ? parsed : ''
    } catch {
      return ''
    }
  }
  return ''
}

const GAP = '__gap__'

/** 366 restaurants at 20 a page is 19 pages — far too many buttons for a phone. */
const pageWindow = (page: number, pageCount: number): Array<number | string> => {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1)
  const wanted = [1, pageCount, page, page - 1, page + 1]
  const sorted = [...new Set(wanted)].filter((n) => n >= 1 && n <= pageCount).sort((a, b) => a - b)
  const out: Array<number | string> = []
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) out.push(GAP)
    out.push(n)
  })
  return out
}

const asList = (payload: unknown): RestaurantRow[] => {
  const p = payload as { restaurants?: unknown; items?: unknown }
  if (Array.isArray(payload)) return payload as RestaurantRow[]
  if (Array.isArray(p?.restaurants)) return p.restaurants as RestaurantRow[]
  if (Array.isArray(p?.items)) return p.items as RestaurantRow[]
  return []
}

export default function RestaurantFinder({
  value, onChange, pageSize = 20
}: { value: unknown; onChange: (value: RestaurantAnswer | null) => void; pageSize?: number }) {
  const { lang, t } = useI18n()
  const [query, setQuery] = useState('')
  const [restaurants, setRestaurants] = useState<RestaurantRow[]>([])
  const [page, setPage] = useState(1)
  const [pageCount, setPageCount] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  // A new search restarts at page 1 — staying on page 3 of the old results
  // while showing new ones is disorienting and usually empty.
  useEffect(() => { setPage(1) }, [query])

  useEffect(() => {
    let alive = true
    // Debounced while typing, immediate for the first load.
    const timer = setTimeout(() => {
      setLoading(true)
      setError(false)
      api.getRestaurants({ search: query.trim(), page, page_size: pageSize })
        .then((payload) => {
          if (!alive) return
          setRestaurants(asList(payload))
          setTotal(Number((payload as { total?: number })?.total || 0))
          setPageCount(Math.max(1, Number((payload as { page_count?: number })?.page_count || 1)))
        })
        .catch((reason) => {
          console.error('[tc][restaurants] search failed', reason)
          if (alive) { setRestaurants([]); setError(true) }
        })
        .finally(() => { if (alive) setLoading(false) })
    }, query.trim() ? 220 : 0)
    return () => { alive = false; clearTimeout(timer) }
  }, [query, page, pageSize])

  const picked = value && typeof value === 'object' ? (value as RestaurantAnswer) : null
  const selectedId = picked ? String(picked.id || picked.restaurant_id || '') : ''
  const selectedLabel = useMemo(() => (picked ? restaurantAnswerLabel(picked, lang) : ''), [picked, lang])

  return (
    <div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('restaurantFinder.searchPlaceholder')}
        className="h-12 w-full rounded-sm border border-gold-dim bg-card px-3 text-md text-ivory placeholder:text-muted-2 focus:border-gold focus:outline-none"
      />
      <p className="mt-2 text-xs text-muted-2">{t('restaurantFinder.pickFromList')}</p>

      {selectedLabel ? (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="mt-3 flex w-full items-center justify-between rounded-sm border border-gold bg-gold-faint px-3 py-2.5 text-left text-13 text-ivory"
        >
          <span>{selectedLabel}</span>
          <span className="text-gold">×</span>
        </button>
      ) : null}

      <p className="mt-3 text-xs text-muted-2">
        {loading
          ? t('restaurantFinder.loading')
          : `${query.trim() ? t('restaurantFinder.results') : t('restaurantFinder.defaultResults')} · ${total}`}
      </p>

      {error ? <p className="mt-3 text-13 text-muted">{t('restaurantFinder.error')}</p> : null}

      <div className="mt-3 space-y-2">
        {restaurants.map((r) => {
          const id = String(r.id || r.restaurant_id)
          const active = Boolean(selectedId) && selectedId === id
          const photo = firstPhoto(r.photo_url)
          const name = lang === 'zh' ? r.name_zh || r.name : r.name || r.name_zh
          const address = lang === 'zh' ? r.address_zh || r.address : r.address || r.address_zh
          const area = r.business_area
          const areaName = area ? (lang === 'zh' ? area.name_zh || area.name_en : area.name_en || area.name_zh) : ''
          return (
            <button
              key={id}
              type="button"
              onClick={() => onChange(restaurantAnswer(r))}
              className={`flex w-full items-stretch gap-3 overflow-hidden rounded-sm border p-2 text-left ${
                active ? 'border-gold bg-gold-faint' : 'border-gold-dim bg-card'
              }`}
            >
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-card-2 text-muted-2">
                {photo
                  ? <img src={photo} alt="" className="h-full w-full object-cover" loading="lazy" />
                  : <span className="text-lg">{String(name || '?').slice(0, 1)}</span>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-13 text-ivory">{name}</p>
                {areaName ? <p className="truncate text-2xs text-gold">{areaName}</p> : null}
                {address ? <p className="truncate text-2xs text-muted-2">{address}</p> : null}
                <div className="mt-0.5 flex gap-3 text-2xs text-muted-2">
                  {r.rating ? <span>{t('restaurantFinder.rating')} {r.rating}</span> : null}
                  {r.price_per_person ? <span>¥{r.price_per_person}</span> : null}
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {!loading && !error && !restaurants.length ? (
        <p className="mt-3 text-13 text-muted">{t('restaurantFinder.empty')}</p>
      ) : null}

      {pageCount > 1 ? (
        <div className="mt-4 flex items-center justify-center gap-1.5">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            className="h-7 w-7 rounded-sm border border-gold-dim text-gold disabled:opacity-45"
          >‹</button>
          {pageWindow(page, pageCount).map((entry, i) =>
            entry === GAP ? (
              <span key={`gap-${i}`} className="px-1 text-muted-2">…</span>
            ) : (
              <button
                key={entry}
                type="button"
                onClick={() => entry !== page && setPage(Number(entry))}
                className={`h-7 min-w-[28px] rounded-sm border px-1.5 text-xs ${
                  entry === page ? 'border-gold bg-gold-faint text-gold' : 'border-gold-dim text-muted'
                }`}
              >{entry}</button>
            )
          )}
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => setPage(page + 1)}
            className="h-7 w-7 rounded-sm border border-gold-dim text-gold disabled:opacity-45"
          >›</button>
        </div>
      ) : null}
    </div>
  )
}
